import { useEffect, useMemo, useRef, useState } from "react";
import { Zap, Search, X, Pencil, Building2, User, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "../../lib/api";
import { eur } from "../../lib/format";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";

const TYPES_CLIENT = ["Particulier", "Professionnel", "Apporteur d'affaires", "Promoteur", "Syndic", "Entreprise", "Autre"];
const TYPE_VENTE = ["Projet", "Vente en direct"];
const STATUTS = [
  { v: "PROSPECTION", l: "Prospection" },
  { v: "DEVIS", l: "Devis envoyé" },
  { v: "ETUDE", l: "Prise de côtes / étude" },
  { v: "NEGOCIATION", l: "Négociation" },
  { v: "COMMANDE", l: "Commande / signé" },
  { v: "LIVRE", l: "Livré / facturé" },
  { v: "PERDU", l: "Perdu" },
];
const SOURCES = [
  "Passage magasin", "Bouche à oreille / recommandation", "Site internet",
  "Prise de rendez-vous en ligne", "Application mobile", "Réseaux sociaux",
  "Client existant / réachat", "Partenaire / apporteur d'affaires",
  "Prospection commerciale", "Business Hunter", "Autre",
];

const EMPTY = {
  client_id: null, client_nom: "", civilite: "", type_client: "Particulier",
  ville: "", email: "", telephone: "",
  type_vente: "Projet", famille: "", chantier_nom: "", montant_estime: "", statut: "DEVIS",
  source_acquisition: "", source_autre: "", date_relance: "", commentaires: "",
};

export default function QuickAddDialog({ open, onOpenChange, onCreated }) {
  const [familles, setFamilles] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [showResults, setShowResults] = useState(false);
  const [selectedClient, setSelectedClient] = useState(null);
  const [dossiers, setDossiers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [editId, setEditId] = useState(null); // dossier en édition
  const [saving, setSaving] = useState(false);
  const debounce = useRef(null);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  useEffect(() => {
    if (open) api.get("/familles").then((r) => setFamilles(r.data)).catch(() => {});
  }, [open]);

  const reset = () => {
    setForm(EMPTY); setQuery(""); setResults([]); setSelectedClient(null);
    setDossiers([]); setSummary(null); setEditId(null); setShowResults(false);
  };

  useEffect(() => { if (!open) reset(); }, [open]);

  useEffect(() => {
    if (debounce.current) clearTimeout(debounce.current);
    if (!query || query.length < 2 || selectedClient) { setResults([]); return; }
    setSearching(true);
    debounce.current = setTimeout(() => {
      api.get(`/clients/search?q=${encodeURIComponent(query)}`)
        .then((r) => { setResults(r.data); setShowResults(true); })
        .catch(() => setResults([]))
        .finally(() => setSearching(false));
    }, 300);
    return () => clearTimeout(debounce.current);
  }, [query, selectedClient]);

  const pickClient = (c) => {
    setSelectedClient(c);
    set("client_id", c.id);
    setShowResults(false);
    setQuery(c.nom);
    api.get(`/clients/${c.id}/dossiers`).then((r) => setDossiers(r.data)).catch(() => setDossiers([]));
    api.get(`/clients/${c.id}/summary`).then((r) => setSummary(r.data)).catch(() => setSummary(null));
  };

  const clearClient = () => {
    setSelectedClient(null); set("client_id", null); setDossiers([]); setSummary(null); setEditId(null);
    setQuery(""); setResults([]);
  };

  const startEdit = (d) => {
    setEditId(d.id);
    setForm((f) => ({
      ...f,
      famille: d.famille || "",
      chantier_nom: d.chantier_nom || "",
      montant_estime: d.montant_total ?? d.total_ht ?? "",
      statut: d.statut || "DEVIS",
      date_relance: (d.date_relance || "").slice(0, 10),
      commentaires: d.commentaires || "",
      type_vente: d.type_vente || "Projet",
    }));
  };

  const familleNames = useMemo(() => familles.map((f) => f.nom), [familles]);

  const submit = async () => {
    if (editId) {
      setSaving(true);
      try {
        await api.patch(`/devis/${editId}/quick`, {
          famille: form.famille,
          chantier_nom: form.chantier_nom,
          montant_estime: Number(form.montant_estime) || 0,
          statut: form.statut,
          date_relance: form.date_relance,
          commentaires: form.commentaires,
        });
        toast.success("Dossier mis à jour");
        onCreated && onCreated();
        onOpenChange(false);
      } catch (e) {
        toast.error(formatApiErrorDetail(e?.response?.data?.detail));
      } finally { setSaving(false); }
      return;
    }
    if (!selectedClient && !form.client_nom.trim()) return toast.error("Nom du client requis");
    setSaving(true);
    try {
      const payload = {
        client_id: selectedClient ? selectedClient.id : null,
        client_nom: selectedClient ? "" : (form.client_nom || query).trim(),
        civilite: form.civilite,
        type_client: form.type_client,
        ville: form.ville, email: form.email, telephone: form.telephone,
        type_vente: form.type_vente, famille: form.famille,
        montant_estime: Number(form.montant_estime) || 0,
        statut: form.statut,
        source_acquisition: form.source_acquisition, source_autre: form.source_autre,
        date_relance: form.date_relance, commentaires: form.commentaires,
      };
      const { data } = await api.post("/quick-dossier", payload);
      if (data.duplicate_warning) {
        toast.warning(data.duplicate_warning.message);
      }
      toast.success(form.type_vente === "Projet" ? "Projet créé" : "Vente directe enregistrée");
      onCreated && onCreated();
      onOpenChange(false);
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail));
    } finally { setSaving(false); }
  };

  const isNewClient = !selectedClient;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#111827] border-[#1E293B] text-slate-100 max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="quickadd-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2">
            <Zap className="h-4 w-4 text-amber-400" /> {editId ? "Modifier le dossier" : "Alimentation rapide"}
          </DialogTitle>
          <DialogDescription className="sr-only">Créer ou modifier rapidement un client et son dossier commercial.</DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* ---- CLIENT ---- */}
          {!editId && (
            <div className="rounded-lg border border-[#1E293B] bg-[#0B0F17] p-3 space-y-3">
              <Label className="text-slate-400 text-xs uppercase tracking-wider">Client</Label>
              {selectedClient ? (
                <>
                <div className="flex items-center justify-between rounded-lg bg-[#111827] border border-amber-500/30 px-3 py-2" data-testid="quickadd-selected-client">
                  <span className="flex items-center gap-2 text-sm font-semibold">
                    {selectedClient.type === "Particulier" ? <User className="h-4 w-4 text-amber-400" /> : <Building2 className="h-4 w-4 text-amber-400" />}
                    {selectedClient.nom} <span className="text-slate-500 font-normal">· {selectedClient.type}</span>
                  </span>
                  <button data-testid="quickadd-clear-client" onClick={clearClient} className="text-slate-400 hover:text-white"><X className="h-4 w-4" /></button>
                </div>
                {summary && (
                  <div className="rounded-lg bg-[#0B0F17] border border-[#1E293B] p-3" data-testid="quickadd-client-summary">
                    <div className="grid grid-cols-3 gap-2 text-center">
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">CA signé</div>
                        <div className="font-mono-metric text-sm font-bold text-emerald-400" data-testid="summary-ca-signe">{eur(summary.ca_signe_ht)}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Dossiers</div>
                        <div className="font-mono-metric text-sm font-bold text-slate-100" data-testid="summary-nb-dossiers">{summary.nb_dossiers}</div>
                      </div>
                      <div>
                        <div className="text-[10px] uppercase tracking-wider text-slate-500">Signés</div>
                        <div className="font-mono-metric text-sm font-bold text-sky-400" data-testid="summary-nb-signes">{summary.nb_signes}</div>
                      </div>
                    </div>
                    {summary.dernier_dossier && (
                      <div className="mt-2 pt-2 border-t border-[#1E293B] flex items-center justify-between text-[11px] text-slate-400" data-testid="summary-dernier">
                        <span className="truncate">Dernier : {summary.dernier_dossier.titre} · <span className="text-slate-500">{summary.dernier_dossier.statut}</span>{summary.dernier_dossier.date ? ` · ${summary.dernier_dossier.date}` : ""}</span>
                        <span className="font-mono-metric text-slate-200 shrink-0">{eur(summary.dernier_dossier.montant)}</span>
                      </div>
                    )}
                    {summary.derniere_activite && (
                      <div className="mt-1 text-[10px] text-slate-500">Dernière activité : {summary.derniere_activite}</div>
                    )}
                  </div>
                )}
                </>
              ) : (
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
                  <Input
                    data-testid="quickadd-client-search"
                    autoFocus
                    placeholder="Nom du client (rechercher ou créer)…"
                    value={query}
                    onChange={(e) => { setQuery(e.target.value); set("client_nom", e.target.value); }}
                    onFocus={() => results.length && setShowResults(true)}
                    className="bg-[#111827] border-[#1E293B] pl-10 pr-8"
                  />
                  {searching && <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500 animate-spin" />}
                  {showResults && results.length > 0 && (
                    <div className="absolute z-30 mt-1 w-full rounded-lg border border-[#1E293B] bg-[#0B0F17] shadow-xl max-h-56 overflow-y-auto" data-testid="quickadd-search-results">
                      {results.map((c) => (
                        <button key={c.id} data-testid={`quickadd-result-${c.id}`} onClick={() => pickClient(c)}
                          className="w-full text-left px-3 py-2 text-sm hover:bg-[#111827] flex items-center justify-between">
                          <span className="font-medium">{c.nom}</span>
                          <span className="text-xs text-slate-500">{c.type}{c.ville ? ` · ${c.ville}` : ""}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {isNewClient && query && (
                <div className="grid grid-cols-2 gap-3 pt-1" data-testid="quickadd-new-client-fields">
                  <div className="space-y-1">
                    <Label className="text-slate-500 text-[11px]">Type de client</Label>
                    <Select value={form.type_client} onValueChange={(v) => set("type_client", v)}>
                      <SelectTrigger data-testid="quickadd-type-client" className="bg-[#111827] border-[#1E293B] h-9"><SelectValue /></SelectTrigger>
                      <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                        {TYPES_CLIENT.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-500 text-[11px]">Ville</Label>
                    <Input value={form.ville} onChange={(e) => set("ville", e.target.value)} className="bg-[#111827] border-[#1E293B] h-9" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-500 text-[11px]">Téléphone</Label>
                    <Input value={form.telephone} onChange={(e) => set("telephone", e.target.value)} className="bg-[#111827] border-[#1E293B] h-9" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-slate-500 text-[11px]">Email</Label>
                    <Input value={form.email} onChange={(e) => set("email", e.target.value)} className="bg-[#111827] border-[#1E293B] h-9" />
                  </div>
                </div>
              )}

              {selectedClient && dossiers.length > 0 && !editId && (
                <div className="pt-1" data-testid="quickadd-existing-dossiers">
                  <Label className="text-slate-500 text-[11px]">Dossiers existants — cliquer pour modifier</Label>
                  <div className="mt-1 space-y-1 max-h-32 overflow-y-auto">
                    {dossiers.map((d) => (
                      <button key={d.id} data-testid={`quickadd-dossier-${d.id}`} onClick={() => startEdit(d)}
                        className="w-full flex items-center justify-between rounded-md bg-[#111827] border border-[#1E293B] px-2.5 py-1.5 text-xs hover:border-amber-500/40">
                        <span className="truncate">{d.famille || d.titre || "Dossier"} · <span className="text-slate-500">{d.statut}</span></span>
                        <span className="flex items-center gap-2 shrink-0"><span className="font-mono-metric">{eur(d.montant_total ?? d.total_ht)}</span><Pencil className="h-3 w-3 text-slate-500" /></span>
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {editId && (
            <div className="flex items-center justify-between rounded-lg border border-sky-500/25 bg-sky-500/5 px-3 py-2 text-sm text-sky-300" data-testid="quickadd-edit-banner">
              <span>Modification d'un dossier existant</span>
              <button onClick={() => setEditId(null)} className="text-slate-400 hover:text-white text-xs underline">créer un nouveau</button>
            </div>
          )}

          {/* ---- DOSSIER ---- */}
          <div className="grid grid-cols-2 gap-3">
            {!editId && (
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Type de vente</Label>
                <Select value={form.type_vente} onValueChange={(v) => set("type_vente", v)}>
                  <SelectTrigger data-testid="quickadd-type-vente" className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                    {TYPE_VENTE.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Famille</Label>
              <Select value={form.famille} onValueChange={(v) => set("famille", v)}>
                <SelectTrigger data-testid="quickadd-famille" className="bg-[#0B0F17] border-[#1E293B]"><SelectValue placeholder="—" /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                  {familleNames.map((f) => <SelectItem key={f} value={f}>{f}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Nom du chantier</Label>
              <Input data-testid="quickadd-chantier" value={form.chantier_nom} onChange={(e) => set("chantier_nom", e.target.value)} className="bg-[#0B0F17] border-[#1E293B]" placeholder="Ex. Rénovation cuisine" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Montant estimé HT (€)</Label>
              <Input data-testid="quickadd-montant" type="number" value={form.montant_estime} onChange={(e) => set("montant_estime", e.target.value)} className="bg-[#0B0F17] border-[#1E293B] font-mono-metric" placeholder="0" />
            </div>
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Statut</Label>
              <Select value={form.statut} onValueChange={(v) => set("statut", v)}>
                <SelectTrigger data-testid="quickadd-statut" className="bg-[#0B0F17] border-[#1E293B]"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                  {STATUTS.map((s) => <SelectItem key={s.v} value={s.v}>{s.l}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-slate-400 text-xs">Prochaine relance</Label>
              <Input data-testid="quickadd-relance" type="date" value={form.date_relance} onChange={(e) => set("date_relance", e.target.value)} className="bg-[#0B0F17] border-[#1E293B]" />
            </div>
            {!editId && (
              <div className="space-y-1">
                <Label className="text-slate-400 text-xs">Source d'acquisition</Label>
                <Select value={form.source_acquisition} onValueChange={(v) => set("source_acquisition", v)}>
                  <SelectTrigger data-testid="quickadd-source" className="bg-[#0B0F17] border-[#1E293B]"><SelectValue placeholder="—" /></SelectTrigger>
                  <SelectContent className="bg-[#111827] border-[#1E293B] text-slate-100">
                    {SOURCES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {!editId && form.source_acquisition === "Autre" && (
              <div className="space-y-1 col-span-2">
                <Label className="text-slate-400 text-xs">Précisez la source</Label>
                <Input data-testid="quickadd-source-autre" value={form.source_autre} onChange={(e) => set("source_autre", e.target.value)} className="bg-[#0B0F17] border-[#1E293B]" />
              </div>
            )}
            <div className="space-y-1 col-span-2">
              <Label className="text-slate-400 text-xs">Commentaire / besoin client</Label>
              <Textarea data-testid="quickadd-commentaire" value={form.commentaires} onChange={(e) => set("commentaires", e.target.value)} className="bg-[#0B0F17] border-[#1E293B] min-h-[64px]" />
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} className="text-slate-400">Annuler</Button>
          <Button data-testid="quickadd-save" onClick={submit} disabled={saving} className="bg-amber-500 hover:bg-amber-600 text-[#0B0F17] font-semibold">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editId ? "Enregistrer" : "Créer le dossier"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
